import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, GripVertical, Trash2 } from 'lucide-react';

// LocalStorage key for remembering column mapping
const STORAGE_KEY = 'cutcalc_csv_column_mapping';

// Available field types that can be mapped
// Keywords are ordered by priority - longer/more specific keywords first
// Short keywords (1-2 chars) require word boundary matching
const FIELD_TYPES = [
  { id: 'width', label: 'Width', required: true, color: 'blue', keywords: ['width', 'section x', 'sect x'] },
  { id: 'length', label: 'Length', required: true, color: 'blue', keywords: ['height', 'length', 'section y', 'sect y'] },
  { id: 'quantity', label: 'Quantity', required: false, color: 'green', keywords: ['quantity', 'qty', 'count', 'pieces', 'pcs', 'num'] },
  { id: 'depth', label: 'Depth', required: false, color: 'purple', keywords: ['depth', 'thickness', 'thk', 'section z', 'sect z', 'height z'] },
  { id: 'name', label: 'Name', required: false, color: 'yellow', keywords: ['name', 'part', 'description', 'desc', 'label', 'item', 'component'] },
  { id: 'grainDirection', label: 'Grain Direction', required: false, color: 'pink', keywords: ['grain', 'orientation'] },
  { id: 'stockType', label: 'Sheet Type', required: false, color: 'orange', keywords: ['material', 'mat', 'wood', 'stock', 'sheet type', 'stock type', 'sheet'] },
  { id: 'sheetDimensions', label: 'Sheet Dimensions', required: false, color: 'teal', keywords: ['sheet dimensions', 'sheet dim', 'sheet size', 'wxlxd', 'dimensions'] }
];

// Unit patterns to detect from headers like "Width (mm)" or "Section X (in)"
const UNIT_PATTERNS = [
  { pattern: /\(mm\)/i, unit: 'mm' },
  { pattern: /\(cm\)/i, unit: 'cm' },
  { pattern: /\(m\)/i, unit: 'm' },
  { pattern: /\(in\)/i, unit: 'in' },
  { pattern: /\(inch(es)?\)/i, unit: 'in' },
  { pattern: /\(ft\)/i, unit: 'ft' },
  { pattern: /\(feet\)/i, unit: 'ft' },
  { pattern: /\bmm\b/i, unit: 'mm' },
  { pattern: /\bcm\b/i, unit: 'cm' },
  { pattern: /\binch(es)?\b/i, unit: 'in' },
];

// Extract unit from header text
const detectUnitFromHeader = (header) => {
  for (const { pattern, unit } of UNIT_PATTERNS) {
    if (pattern.test(header)) {
      return unit;
    }
  }
  return null;
};

// Strip unit suffixes from header for cleaner keyword matching
const stripUnitFromHeader = (header) => {
  return header
    .replace(/\s*\([^)]*\)\s*/g, ' ')  // Remove parenthetical content like (mm)
    .replace(/\s+/g, ' ')               // Normalize whitespace
    .trim();
};

// Color mapping for field types
const COLOR_STYLES = {
  blue: 'bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200',
  purple: 'bg-purple-100 text-purple-800 border-purple-300 hover:bg-purple-200',
  green: 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200',
  yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-200',
  orange: 'bg-orange-100 text-orange-800 border-orange-300 hover:bg-orange-200',
  pink: 'bg-pink-100 text-pink-800 border-pink-300 hover:bg-pink-200',
  teal: 'bg-teal-100 text-teal-800 border-teal-300 hover:bg-teal-200'
};

// DropZone for a specific column position
const ColumnDropZone = ({ columnNumber, mappedField, headerValue, onDragEnter, onDragOver, onDrop, onRemove, getSampleValue }) => {
  const fieldInfo = mappedField ? FIELD_TYPES.find(f => f.id === mappedField) : null;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Column {columnNumber}
      </label>
      <div
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDrop={(e) => onDrop(e, columnNumber)}
        className={`
          min-h-[70px] px-4 py-3 border-2 border-dashed rounded-lg
          ${mappedField
            ? 'bg-green-50 border-green-400'
            : 'bg-gray-50 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
          }
          transition-colors
        `}
      >
        {mappedField && fieldInfo ? (
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-2 py-1 rounded text-xs font-semibold ${COLOR_STYLES[fieldInfo.color]}`}>
                  {fieldInfo.label}
                  {fieldInfo.required && <span className="ml-1">*</span>}
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-1">
                Sample: <span className="font-mono bg-white px-1.5 py-0.5 rounded border">{getSampleValue(columnNumber - 1)}</span>
              </p>
            </div>
            <button
              onClick={() => onRemove(columnNumber)}
              className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
              title="Remove mapping"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="text-center py-1">
            <p className="text-gray-400 text-xs mb-1">Drop field here</p>
            {headerValue && (
              <p className="font-mono text-xs bg-white px-2 py-1 rounded border border-gray-200 text-gray-600 truncate" title={headerValue}>
                "{headerValue}"
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const CSVColumnMapper = ({ isOpen, onClose, onConfirm, headers, sampleData }) => {
  // State: columnNumber -> fieldId mapping (e.g., {1: 'width', 2: 'height', 3: 'quantity'})
  const [columnMapping, setColumnMapping] = useState({});
  const [draggedField, setDraggedField] = useState(null);
  const [errors, setErrors] = useState([]);
  const [numColumns, setNumColumns] = useState(10);
  const [detectedUnit, setDetectedUnit] = useState(null);

  // Load saved mapping from localStorage
  const loadSavedMapping = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.warn('Failed to load saved column mapping:', e);
    }
    return null;
  };

  // Save mapping to localStorage
  const saveMapping = (mapping) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(mapping));
    } catch (e) {
      console.warn('Failed to save column mapping:', e);
    }
  };

  // Auto-detect columns using keyword matching
  const autoDetectColumns = (headers) => {
    const mapping = {};
    const usedFields = new Set();
    let foundUnit = null;

    headers.forEach((header, index) => {
      const colNum = index + 1; // Convert to 1-based

      // Try to detect unit from this header
      if (!foundUnit) {
        foundUnit = detectUnitFromHeader(header);
      }

      // Strip unit info and normalize for matching
      const cleanHeader = stripUnitFromHeader(header).toLowerCase();

      // Find best matching field type
      for (const fieldType of FIELD_TYPES) {
        if (usedFields.has(fieldType.id)) continue; // Each field can only be used once

        // Check for keyword matches
        const hasMatch = fieldType.keywords.some(keyword => {
          // Exact match
          if (cleanHeader === keyword) return true;
          // Contains as a whole word (word boundary matching)
          const wordBoundaryRegex = new RegExp(`\\b${keyword}\\b`, 'i');
          if (wordBoundaryRegex.test(cleanHeader)) return true;
          return false;
        });

        if (hasMatch) {
          mapping[colNum] = fieldType.id;
          usedFields.add(fieldType.id);
          break;
        }
      }
    });

    return { mapping, unit: foundUnit };
  };

  // Auto-detect columns on mount or when headers change
  useEffect(() => {
    if (headers && headers.length > 0) {
      setNumColumns(Math.max(headers.length, 10));

      // First try auto-detection from headers
      const { mapping: autoMapping, unit: autoUnit } = autoDetectColumns(headers);

      // Set detected unit if found
      if (autoUnit) {
        setDetectedUnit(autoUnit);
      }

      // If auto-detection found required fields, use it
      const autoMappedFields = Object.values(autoMapping);
      const hasRequiredFields = autoMappedFields.includes('width') && autoMappedFields.includes('length');

      if (hasRequiredFields) {
        setColumnMapping(autoMapping);
      } else {
        // Fall back to saved mapping if auto-detection didn't find required fields
        const savedMapping = loadSavedMapping();
        if (savedMapping && Object.keys(savedMapping).length > 0) {
          // Filter saved mapping to only include columns that exist in current CSV
          const filteredMapping = {};
          Object.entries(savedMapping).forEach(([colNum, fieldId]) => {
            if (parseInt(colNum) <= headers.length) {
              filteredMapping[colNum] = fieldId;
            }
          });
          setColumnMapping(filteredMapping);
        } else {
          setColumnMapping(autoMapping);
        }
      }
    }
  }, [headers]);

  const validateMapping = () => {
    const newErrors = [];
    const mappedFields = Object.values(columnMapping);

    if (!mappedFields.includes('width')) {
      newErrors.push('Width field is required - drag it to a column');
    }
    if (!mappedFields.includes('length')) {
      newErrors.push('Length field is required - drag it to a column');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleConfirm = () => {
    if (validateMapping()) {
      // Save the mapping for future use
      saveMapping(columnMapping);

      // Convert column->field mapping to field->columnIndex mapping
      const fieldMapping = {
        width: '',
        length: '',
        depth: '',
        quantity: '',
        name: '',
        material: '',
        grainDirection: '',
        sheetDimensions: '',
        detectedUnit: detectedUnit // Include detected unit for conversion
      };

      Object.entries(columnMapping).forEach(([colNum, fieldId]) => {
        // Convert column number (1-based) to 0-based index
        fieldMapping[fieldId] = (parseInt(colNum) - 1).toString();
      });

      onConfirm(fieldMapping);
      onClose();
    }
  };

  const handleDragStart = (e, fieldId) => {
    e.stopPropagation();
    setDraggedField(fieldId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', fieldId);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, columnNumber) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedField) {
      // Remove field from any existing column
      const newMapping = { ...columnMapping };
      Object.keys(newMapping).forEach(key => {
        if (newMapping[key] === draggedField) {
          delete newMapping[key];
        }
      });

      // Add field to new column
      newMapping[columnNumber] = draggedField;
      setColumnMapping(newMapping);
      setDraggedField(null);
    }
  };

  const handleRemoveMapping = (columnNumber) => {
    const newMapping = { ...columnMapping };
    delete newMapping[columnNumber];
    setColumnMapping(newMapping);
  };

  const getSampleValue = (columnIndex) => {
    if (sampleData && sampleData.length > 0 && sampleData[0][columnIndex]) {
      return sampleData[0][columnIndex] || 'N/A';
    }
    return 'N/A';
  };

  const getUsedFields = () => {
    return Object.values(columnMapping);
  };

  // Prevent drag events from bubbling to global handlers
  const stopDragPropagation = (e) => {
    e.stopPropagation();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onDragEnter={stopDragPropagation}
      onDragOver={stopDragPropagation}
      onDragLeave={stopDragPropagation}
      onDrop={stopDragPropagation}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-blue-600" />
            Map CSV Columns
          </h2>
          <div className="flex items-center gap-3">
            {detectedUnit && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-full text-xs text-blue-800">
                <CheckCircle className="w-3 h-3 text-blue-600" />
                Detected unit: <strong>{detectedUnit.toUpperCase()}</strong>
              </span>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">

          {/* Error Display */}
          {errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <ul className="text-red-700 text-sm">
                  {errors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Column Mapping as inline comma-separated fields */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Column Order</h3>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex flex-wrap items-center gap-1">
                {headers.map((header, index) => {
                  const colNum = index + 1;
                  const mappedFieldId = columnMapping[colNum];
                  const fieldInfo = mappedFieldId ? FIELD_TYPES.find(f => f.id === mappedFieldId) : null;
                  const sample = getSampleValue(index);

                  return (
                    <React.Fragment key={colNum}>
                      <button
                        onClick={() => {
                          // Cycle through field types on tap
                          const usedFields = Object.values(columnMapping);
                          const availableFields = FIELD_TYPES.filter(f => !usedFields.includes(f.id) || f.id === mappedFieldId);
                          const currentIdx = mappedFieldId ? availableFields.findIndex(f => f.id === mappedFieldId) : -1;
                          const nextIdx = (currentIdx + 1) % (availableFields.length + 1);
                          const newMapping = { ...columnMapping };
                          if (nextIdx === availableFields.length) {
                            // Unassign
                            delete newMapping[colNum];
                          } else {
                            // Remove old assignment of this field
                            Object.keys(newMapping).forEach(key => {
                              if (newMapping[key] === availableFields[nextIdx].id) delete newMapping[key];
                            });
                            newMapping[colNum] = availableFields[nextIdx].id;
                          }
                          setColumnMapping(newMapping);
                        }}
                        className={`inline-flex flex-col items-center px-3 py-1.5 rounded-lg border text-xs transition-colors ${
                          fieldInfo
                            ? COLOR_STYLES[fieldInfo.color]
                            : 'bg-white border-gray-300 text-gray-500 hover:border-gray-400'
                        }`}
                        title={`Header: "${header}" | Sample: ${sample}\nClick to change field type`}
                      >
                        <span className="font-semibold">{fieldInfo ? fieldInfo.label : header || `Col ${colNum}`}</span>
                        <span className="font-mono text-[10px] opacity-70 truncate max-w-[80px]">{isNaN(sample) ? sample : parseFloat(parseFloat(sample).toFixed(3))}</span>
                      </button>
                      {index < headers.length - 1 && (
                        <span className="text-gray-300 text-lg font-light">,</span>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-3">Select column to cycle its field type</p>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-1.5">
            {FIELD_TYPES.map(field => {
              const isUsed = Object.values(columnMapping).includes(field.id);
              return (
                <span
                  key={field.id}
                  className={`text-xs px-2 py-0.5 rounded-full border ${
                    isUsed ? COLOR_STYLES[field.color] : 'bg-gray-100 text-gray-400 border-gray-200'
                  }`}
                >
                  {field.label}{field.required ? '*' : ''}
                </span>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Import with this mapping
          </button>
        </div>
      </div>
    </div>
  );
};

export default CSVColumnMapper;
